import { useMemo } from 'react';

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0), f(8), f(4)];
}

function parseHslString(hslStr: string): [number, number, number] | null {
  const parts = hslStr.trim().replace(/%/g, '').split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return [parts[0], parts[1], parts[2]];
}

function getRelativeLuminance([r, g, b]: [number, number, number]): number {
  const [sR, sG, sB] = [r, g, b].map((val) =>
    val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB;
}

// useContrastChecker computes WCAG AA/AAA contrast ratios over semantic HSL tokens.
export function useContrastChecker(
  fgHsl: string,
  bgHsl: string
): { ratio: number; passesAA: boolean; passesAAA: boolean } {
  return useMemo(() => {
    const fg = parseHslString(fgHsl);
    const bg = parseHslString(bgHsl);
    if (!fg || !bg) {
      return { ratio: 1, passesAA: false, passesAAA: false };
    }

    const fgRgb = hslToRgb(...fg);
    const bgRgb = hslToRgb(...bg);
    const l1 = getRelativeLuminance(fgRgb);
    const l2 = getRelativeLuminance(bgRgb);

    const brighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    const rawRatio = (brighter + 0.05) / (darker + 0.05);
    const ratio = Math.round(rawRatio * 100) / 100;

    return {
      ratio,
      passesAA: ratio >= 4.5,
      passesAAA: ratio >= 7.0,
    };
  }, [fgHsl, bgHsl]);
}
